import React, { useEffect } from 'react';
import { Link } from 'wouter';
import { Shield, FileText, Mail, ArrowLeft, Clock, Printer } from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import {
    isLegalDocsPublished,
    getPrivacyPolicyMetadata,
    getTermsOfServiceMetadata,
    type LegalDocumentMetadata,
    type LegalContentBlock,
} from '../config/legalConfig';

interface LegalDocumentPageProps {
    document: LegalDocumentMetadata;
}

function renderBlock(block: LegalContentBlock, idx: number): React.ReactNode {
    if (block.type === 'paragraph') {
        return (
            <p key={idx} className="text-sm sm:text-base text-slate-700 leading-relaxed">
                {block.text}
            </p>
        );
    }

    if (block.type === 'list') {
        if (block.ordered) {
            return (
                <ol
                    key={idx}
                    className="list-decimal pl-6 space-y-2 text-sm sm:text-base text-slate-700 leading-relaxed"
                >
                    {block.items.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ol>
            );
        }
        return (
            <ul
                key={idx}
                className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-700 leading-relaxed"
            >
                {block.items.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>
        );
    }

    if (block.type === 'table') {
        return (
            <div
                key={idx}
                className="overflow-x-auto rounded-xl border border-slate-200 my-4 print:overflow-visible"
            >
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs sm:text-sm">
                    <thead className="bg-slate-100 text-slate-900 font-semibold">
                        <tr>
                            {block.headers.map((header, hIdx) => (
                                <th key={hIdx} scope="col" className="px-4 py-3 align-top">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white text-slate-700">
                        {block.rows.map((row, rIdx) => (
                            <tr key={rIdx} className="hover:bg-slate-50/60">
                                {row.map((cell, cIdx) => (
                                    <td
                                        key={cIdx}
                                        className={`px-4 py-3 align-top leading-relaxed ${
                                            cIdx === 0 ? 'font-medium text-slate-900 sm:w-1/4' : ''
                                        }`}
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return null;
}

export const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ document: doc }) => {
    const published = isLegalDocsPublished() && doc.approvalStatus === 'approved' && doc.sections.length > 0;

    useEffect(() => {
        window.scrollTo(0, 0);
        window.document.title = published
            ? `${doc.title} — BayWhiz`
            : `${doc.shortTitle} (Pending Publication) — BayWhiz`;
    }, [doc.title, doc.shortTitle, published]);

    const Icon = doc.slug === '/privacy' ? Shield : FileText;

    return (
        <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 print:bg-white">
            <div className="print:hidden">
                <MarketingNav variant="light" />
            </div>

            <main id="main-content" className="flex-1 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 print:py-4 print:px-0">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-6 flex items-center justify-between gap-4 print:hidden">
                        <Link
                            href="/"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-1 py-0.5"
                        >
                            <ArrowLeft size={16} />
                            Back to BayWhiz Home
                        </Link>

                        {published && (
                            <button
                                type="button"
                                onClick={() => window.print()}
                                data-testid="legal-doc-print-button"
                                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 rounded-lg px-3 py-1.5 shadow-sm transition-colors cursor-pointer"
                            >
                                <Printer size={15} />
                                Print / Save PDF
                            </button>
                        )}
                    </div>

                    <article
                        aria-labelledby="legal-doc-title"
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden print:border-0 print:shadow-none print:rounded-none"
                    >
                        {/* Header */}
                        <header className="border-b border-slate-200 bg-slate-900 text-white px-6 py-8 sm:px-10 sm:py-10 print:bg-white print:text-slate-900 print:px-0 print:py-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/15 border border-teal-400/30 text-teal-300 text-xs font-semibold uppercase tracking-wider mb-4 print:hidden">
                                <Icon size={14} />
                                <span>Legal &amp; Compliance</span>
                            </div>
                            <h1 id="legal-doc-title" className="text-2xl sm:text-3xl font-bold tracking-tight">
                                {doc.title}
                            </h1>

                            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-slate-300 print:text-slate-700">
                                {published && doc.effectiveDate && (
                                    <span data-testid="legal-doc-effective-date">
                                        <strong className="text-white print:text-slate-900">Effective date:</strong>{' '}
                                        {doc.effectiveDate}
                                    </span>
                                )}
                                {published && doc.version && (
                                    <span data-testid="legal-doc-version">
                                        <strong className="text-white print:text-slate-900">Version:</strong>{' '}
                                        {doc.version}
                                    </span>
                                )}
                                {published && doc.lastUpdated && (
                                    <span>
                                        <strong className="text-white print:text-slate-900">Last Updated:</strong>{' '}
                                        {doc.lastUpdated}
                                    </span>
                                )}
                                {!published && (
                                    <span
                                        data-testid="legal-doc-status-badge"
                                        className="inline-flex items-center gap-1.5 text-amber-300 font-medium"
                                    >
                                        <Clock size={14} />
                                        Pending Final Legal Publication
                                    </span>
                                )}
                            </div>
                        </header>

                        {/* Body */}
                        <div className="px-6 py-8 sm:px-10 sm:py-10 print:px-0 print:py-6">
                            {!published ? (
                                <section
                                    role="status"
                                    aria-live="polite"
                                    data-testid="legal-doc-unpublished-gate"
                                    className="space-y-6"
                                >
                                    <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-5 sm:p-6">
                                        <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">
                                            {doc.shortTitle} Pending Final Publication
                                        </h2>
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            BayWhiz&apos;s official {doc.shortTitle} is currently undergoing final legal
                                            review prior to public publication. To ensure accuracy and transparency,
                                            BayWhiz does not publish draft or placeholder legal provisions as final
                                            binding terms.
                                        </p>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 sm:p-6 space-y-3">
                                        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600">
                                            Questions or Early-Access Inquiries
                                        </h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            If you are participating in BayWhiz&apos;s controlled 30-day warehouse trial
                                            or have questions regarding data handling, privacy, or terms of service,
                                            please contact our team directly:
                                        </p>
                                        <div className="pt-1">
                                            <a
                                                href="mailto:support@baywhiz.com"
                                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800 underline underline-offset-4"
                                            >
                                                <Mail size={16} />
                                                support@baywhiz.com
                                            </a>
                                        </div>
                                    </div>
                                </section>
                            ) : (
                                <div className="space-y-8" data-testid="legal-doc-published-content">
                                    {doc.preambleParagraphs && doc.preambleParagraphs.length > 0 && (
                                        <div
                                            data-testid="legal-doc-preamble"
                                            className="space-y-3 border-b border-slate-200 pb-6"
                                        >
                                            {doc.preambleParagraphs.map((para, idx) => (
                                                <p
                                                    key={idx}
                                                    className="text-sm sm:text-base text-slate-700 leading-relaxed"
                                                >
                                                    {para}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {doc.sections.length > 1 && (
                                        <nav
                                            aria-label={`${doc.shortTitle} Table of Contents`}
                                            className="rounded-xl border border-slate-200 bg-slate-50 p-5 print:hidden"
                                        >
                                            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                                                Contents
                                            </h2>
                                            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                                {doc.sections.map((sec, index) => (
                                                    <li key={sec.id}>
                                                        <a
                                                            href={`#${sec.id}`}
                                                            className="text-teal-700 hover:text-teal-800 hover:underline font-medium"
                                                        >
                                                            {index + 1}. {sec.heading}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ol>
                                        </nav>
                                    )}

                                    {doc.sections.map((sec, index) => (
                                        <section key={sec.id} id={sec.id} className="space-y-3 scroll-mt-24">
                                            <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                                                {index + 1}. {sec.heading}
                                            </h2>
                                            {sec.blocks && sec.blocks.length > 0
                                                ? sec.blocks.map((block, bIdx) => renderBlock(block, bIdx))
                                                : sec.paragraphs.map((paragraph, pIdx) => (
                                                      <p
                                                          key={pIdx}
                                                          className="text-sm sm:text-base text-slate-700 leading-relaxed"
                                                      >
                                                          {paragraph}
                                                      </p>
                                                  ))}
                                        </section>
                                    ))}
                                </div>
                            )}
                        </div>
                    </article>
                </div>
            </main>

            <div className="print:hidden">
                <MarketingFooter variant="light" />
            </div>
        </div>
    );
};

export const PrivacyPolicyPage: React.FC = () => (
    <LegalDocumentPage document={getPrivacyPolicyMetadata()} />
);

export const TermsOfServicePage: React.FC = () => (
    <LegalDocumentPage document={getTermsOfServiceMetadata()} />
);

export default LegalDocumentPage;
